// CSR-823: Add / Edit Insurance Company modal. Used from the list view
// (CSR-822) and from the detail-page Edit button (CSR-824 / CSR-825).
//
// Conditional fields per the PRD:
//   * Category = Parent Company → hide parent-company dropdown AND both
//     commission fields. Backend rejects non-null values for these on
//     parent rows, so the submit payload sends `null` for them too.
//   * Category = Insurance Carrier | MGA → show parent dropdown
//     (populated from existing parent_company rows) and both commission
//     fields.
//
// Form-state shape mirrors CertificateHolderFormDialog: local state +
// Zod validation on submit, errors rendered inline. The submit payload
// is built explicitly per category so the backend's business-rule
// validator never sees the "wrong" combination of fields.

import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  useCreateInsuranceCompany,
  useInsuranceCompanies,
  useUpdateInsuranceCompany,
  type InsertInsuranceCompanyClient,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { US_STATES } from "@shared/schema";
import type {
  InsuranceCompany,
  InsuranceCompanyCategory,
  UpdateInsuranceCompany,
} from "@shared/schema";

const NO_PARENT_VALUE = "__none__";
const STATE_VALUES: readonly string[] = US_STATES.map((s) => s.value);

interface FormState {
  name: string;
  category: InsuranceCompanyCategory | "";
  parentCompanyId: string; // empty string = none
  naic: string;
  website: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  newCommissionPct: string;
  renewalCommissionPct: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  category: "",
  parentCompanyId: "",
  naic: "",
  website: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  newCommissionPct: "",
  renewalCommissionPct: "",
};

const CATEGORY_LABELS: Record<InsuranceCompanyCategory, string> = {
  insurance_carrier: "Insurance Carrier",
  mga: "MGA",
  parent_company: "Parent Company",
};

function fromCompany(company: InsuranceCompany): FormState {
  return {
    name: company.name ?? "",
    category: company.category,
    parentCompanyId: company.parentCompanyId ?? "",
    naic: company.naic ?? "",
    website: company.website ?? "",
    street: company.street ?? "",
    city: company.city ?? "",
    state: company.state ?? "",
    zip: company.zip ?? "",
    newCommissionPct:
      company.newCommissionPct == null ? "" : String(company.newCommissionPct),
    renewalCommissionPct:
      company.renewalCommissionPct == null ? "" : String(company.renewalCommissionPct),
  };
}

// Zod gate runs on submit. Required fields: name, category. Conditional
// constraints (digits-only NAIC, URL-shaped website, 0-100 commissions)
// fire only when the user actually typed something.
const naicSchema = z
  .string()
  .trim()
  .regex(/^\d+$/, "NAIC must contain digits only");
const websiteSchema = z.string().trim().url("Enter a valid URL");
const commissionSchema = z
  .string()
  .trim()
  .refine((v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  }, "Must be between 0 and 100");
const stateSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || STATE_VALUES.includes(v), "Select a valid US state");

type FieldErrors = Partial<Record<keyof FormState, string>>;

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.category) errors.category = "Category is required";

  if (form.naic.trim() && !naicSchema.safeParse(form.naic).success) {
    errors.naic = "NAIC must contain digits only";
  }
  if (form.website.trim() && !websiteSchema.safeParse(form.website).success) {
    errors.website = "Enter a valid URL";
  }
  if (form.state.trim() && !stateSchema.safeParse(form.state).success) {
    errors.state = "Select a valid US state";
  }
  if (form.category !== "parent_company") {
    if (
      form.newCommissionPct.trim() &&
      !commissionSchema.safeParse(form.newCommissionPct).success
    ) {
      errors.newCommissionPct = "Must be between 0 and 100";
    }
    if (
      form.renewalCommissionPct.trim() &&
      !commissionSchema.safeParse(form.renewalCommissionPct).success
    ) {
      errors.renewalCommissionPct = "Must be between 0 and 100";
    }
  }
  return errors;
}

function toCreatePayload(form: FormState): InsertInsuranceCompanyClient {
  const category = form.category as InsuranceCompanyCategory;
  const isParent = category === "parent_company";
  return {
    name: form.name.trim(),
    category,
    parentCompanyId: isParent ? null : form.parentCompanyId || null,
    naic: form.naic.trim() || null,
    website: form.website.trim() || null,
    street: form.street.trim() || null,
    city: form.city.trim() || null,
    state: form.state.trim() || null,
    zip: form.zip.trim() || null,
    newCommissionPct: isParent ? null : form.newCommissionPct.trim() || null,
    renewalCommissionPct: isParent
      ? null
      : form.renewalCommissionPct.trim() || null,
  };
}

function toUpdatePayload(form: FormState): UpdateInsuranceCompany {
  // Update uses the same body shape -- backend's
  // updateInsuranceCompanySchema is the insert schema partial-ized, and
  // its business-rule validator enforces the same parent_company
  // constraints when the category is being changed.
  return toCreatePayload(form) as UpdateInsuranceCompany;
}

// CSR-1652 — the mutation hooks throw `new Error(body.error || title)`, so a
// generic server failure (or any future failure mode that doesn't set a more
// specific message) can produce an error whose `.message` is identical to
// the toast title. Since TOAST_LIMIT is 1, that renders as one toast with
// the same sentence twice. Only show a description when it actually adds
// information beyond the title.
function buildSaveErrorToastDescription(
  title: string,
  err: unknown,
): string | undefined {
  if (!(err instanceof Error)) return undefined;
  if (err.message === title) return undefined;
  return err.message;
}

interface InsuranceCompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  company?: InsuranceCompany | null;
  onSaved?: (company: InsuranceCompany) => void;
}

export function InsuranceCompanyFormDialog({
  open,
  onOpenChange,
  mode,
  company,
  onSaved,
}: InsuranceCompanyFormDialogProps) {
  const { toast } = useToast();
  const { data: listData } = useInsuranceCompanies();
  const parentOptions = listData?.parentCompanies ?? [];

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const createMutation = useCreateInsuranceCompany();
  const updateMutation = useUpdateInsuranceCompany();

  // Reset / prefill whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      setForm(EMPTY_FORM);
    } else if (company) {
      setForm(fromCompany(company));
    }
    setErrors({});
  }, [open, mode, company]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const isParent = form.category === "parent_company";
  const showCarrierFields = form.category === "insurance_carrier" || form.category === "mga";

  // Editing self: filter out the row itself from parent options so a
  // parent can't (re)parent to itself.
  const parentDropdownOptions = useMemo(() => {
    if (mode === "edit" && company) {
      return parentOptions.filter((p) => p.id !== company.id);
    }
    return parentOptions;
  }, [mode, company, parentOptions]);

  const canSubmit = form.name.trim().length > 0 && !!form.category && !isPending;

  const submit = () => {
    const validation = validate(form);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    if (mode === "create") {
      const payload = toCreatePayload(form);
      createMutation.mutate(payload, {
        onSuccess: (saved) => {
          toast({ title: "Insurance company created successfully" });
          onSaved?.(saved);
          onOpenChange(false);
        },
        onError: (err) => {
          const title = "Failed to create insurance company";
          toast({
            title,
            description: buildSaveErrorToastDescription(title, err),
            variant: "destructive",
          });
        },
      });
    } else if (company) {
      const payload = toUpdatePayload(form);
      updateMutation.mutate(
        { id: company.id, data: payload },
        {
          onSuccess: (saved) => {
            toast({ title: "Insurance company updated successfully" });
            onSaved?.(saved);
            onOpenChange(false);
          },
          onError: (err) => {
            const title = "Failed to update insurance company";
            toast({
              title,
              description: buildSaveErrorToastDescription(title, err),
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (canSubmit) submit();
          }
        }}
        data-testid="dialog-insurance-company-form"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "New Insurance Company" : "Edit Insurance Company"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === "create"
              ? "Create a new insurance company."
              : "Update this insurance company."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="ic-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ic-name"
              autoFocus={mode === "create"}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="input-ic-name"
            />
            {errors.name && (
              <p className="text-xs text-destructive" data-testid="error-ic-name">
                {errors.name}
              </p>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="ic-category">
              Category <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.category || undefined}
              onValueChange={(v) => set("category", v as InsuranceCompanyCategory)}
            >
              <SelectTrigger id="ic-category" data-testid="select-ic-category">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="insurance_carrier">{CATEGORY_LABELS.insurance_carrier}</SelectItem>
                <SelectItem value="mga">{CATEGORY_LABELS.mga}</SelectItem>
                <SelectItem value="parent_company">{CATEGORY_LABELS.parent_company}</SelectItem>
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-xs text-destructive" data-testid="error-ic-category">
                {errors.category}
              </p>
            )}
          </div>

          {/* Parent Company — hidden when Category = Parent Company */}
          {showCarrierFields && (
            <div className="space-y-2" data-testid="row-parent-company">
              <Label htmlFor="ic-parent">Parent Company</Label>
              <Select
                value={form.parentCompanyId || NO_PARENT_VALUE}
                onValueChange={(v) =>
                  set("parentCompanyId", v === NO_PARENT_VALUE ? "" : v)
                }
              >
                <SelectTrigger id="ic-parent" data-testid="select-ic-parent">
                  <SelectValue placeholder="No parent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PARENT_VALUE}>No parent</SelectItem>
                  {parentDropdownOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* NAIC + Website */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ic-naic">NAIC Code</Label>
              <Input
                id="ic-naic"
                inputMode="numeric"
                value={form.naic}
                onChange={(e) => set("naic", e.target.value)}
                data-testid="input-ic-naic"
              />
              {errors.naic && (
                <p className="text-xs text-destructive" data-testid="error-ic-naic">
                  {errors.naic}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ic-website">Website</Label>
              <Input
                id="ic-website"
                placeholder="https://example.com"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                data-testid="input-ic-website"
              />
              {errors.website && (
                <p className="text-xs text-destructive" data-testid="error-ic-website">
                  {errors.website}
                </p>
              )}
            </div>
          </div>

          {/* Address: Street, City, State, ZIP */}
          <div className="space-y-2">
            <Label htmlFor="ic-street">Street</Label>
            <Input
              id="ic-street"
              value={form.street}
              onChange={(e) => set("street", e.target.value)}
              data-testid="input-ic-street"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ic-city">City</Label>
              <Input
                id="ic-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                data-testid="input-ic-city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ic-state">State</Label>
              <Select
                value={form.state || undefined}
                onValueChange={(v) => set("state", v)}
              >
                <SelectTrigger id="ic-state" data-testid="select-ic-state">
                  <SelectValue placeholder="Select state..." />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.state && (
                <p className="text-xs text-destructive" data-testid="error-ic-state">
                  {errors.state}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ic-zip">ZIP</Label>
              <Input
                id="ic-zip"
                value={form.zip}
                onChange={(e) => set("zip", e.target.value)}
                data-testid="input-ic-zip"
              />
            </div>
          </div>

          {/* Commissions — hidden when Category = Parent Company */}
          {!isParent && form.category && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              data-testid="row-commissions"
            >
              <div className="space-y-2">
                <Label htmlFor="ic-new-commission">New Commission %</Label>
                <Input
                  id="ic-new-commission"
                  inputMode="decimal"
                  value={form.newCommissionPct}
                  onChange={(e) => set("newCommissionPct", e.target.value)}
                  data-testid="input-ic-new-commission"
                />
                {errors.newCommissionPct && (
                  <p
                    className="text-xs text-destructive"
                    data-testid="error-ic-new-commission"
                  >
                    {errors.newCommissionPct}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ic-renewal-commission">Renewal Commission %</Label>
                <Input
                  id="ic-renewal-commission"
                  inputMode="decimal"
                  value={form.renewalCommissionPct}
                  onChange={(e) => set("renewalCommissionPct", e.target.value)}
                  data-testid="input-ic-renewal-commission"
                />
                {errors.renewalCommissionPct && (
                  <p
                    className="text-xs text-destructive"
                    data-testid="error-ic-renewal-commission"
                  >
                    {errors.renewalCommissionPct}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="button-ic-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full gap-2"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="button-ic-submit"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "create" ? "Create" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
